/**
 * Analytics Tab Component
 * 
 * Displays trip analytics including spending trends, category breakdowns, and statistics
 */

import { useState, useEffect, useMemo } from 'react';
import { View, ScrollView, ActivityIndicator } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { Card } from '@/components/ui/Card';
import { ErrorState } from '@/components/ui/ErrorState';
import { ThemedText, SubHeading, Body, Caption } from '@/components/ui/Text';
import { useTheme } from '@/theme';
import type { Trip } from '@/services/trips';
import type { Expense } from '@/services/expenses';
import type { Booking } from '@/services/bookings';
import {
  calculateTripStatistics,
  calculateCategorySpending,
  calculateDailySpending,
  calculateSpendingTrends,
  type TripStatistics,
  type CategorySpending,
  type DailySpending,
  type SpendingTrend,
} from '@/utils/tripAnalytics';
import { formatCurrency } from '@/utils/currency';

type AnalyticsTabProps = {
  trip: Trip;
  expenses: Expense[];
  bookings: Booking[];
};

export function AnalyticsTab({ trip, expenses, bookings }: AnalyticsTabProps) {
  const theme = useTheme();
  const [loading, setLoading] = useState(true);
  const [statistics, setStatistics] = useState<TripStatistics | null>(null);
  const [categorySpending, setCategorySpending] = useState<CategorySpending[]>([]);
  const [dailySpending, setDailySpending] = useState<DailySpending[]>([]);
  const [spendingTrends, setSpendingTrends] = useState<SpendingTrend[]>([]);
  const [selectedPeriod, setSelectedPeriod] = useState<'daily' | 'weekly'>('daily');
  const [analyticsError, setAnalyticsError] = useState<string | null>(null);
  const [retryNonce, setRetryNonce] = useState(0);

  // Calculate analytics when data changes
  useEffect(() => {
    const calculateAnalytics = async () => {
      setAnalyticsError(null);
      if (!trip || expenses.length === 0) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        
        const [stats, categories, daily, trends] = await Promise.all([
          calculateTripStatistics(trip, expenses, bookings),
          calculateCategorySpending(expenses, trip.baseCurrency || 'USD'),
          calculateDailySpending(expenses, trip.baseCurrency || 'USD'),
          calculateSpendingTrends(
            expenses,
            trip.startDate,
            trip.endDate,
            trip.baseCurrency || 'USD',
            selectedPeriod
          ),
        ]);

        setStatistics(stats);
        setCategorySpending(categories);
        setDailySpending(daily);
        setSpendingTrends(trends);
      } catch (error) {
        console.error('[AnalyticsTab] Error calculating analytics:', error);
        setStatistics(null);
        setCategorySpending([]);
        setDailySpending([]);
        setSpendingTrends([]);
        setAnalyticsError("Couldn't load analytics. Check your connection and try again.");
      } finally {
        setLoading(false);
      }
    };

    calculateAnalytics();
  }, [trip, expenses, bookings, selectedPeriod, retryNonce]);

  const categoryColors: Record<string, string> = {
    flight: '#3B82F6',
    lodging: '#8B5CF6',
    food: '#F59E0B',
    transport: '#10B981',
    activity: '#EF4444',
    shopping: '#EC4899',
    other: '#6B7280',
  };

  const categoryLabels: Record<string, string> = {
    flight: 'Flights',
    lodging: 'Lodging',
    food: 'Food & Dining',
    transport: 'Transportation',
    activity: 'Activities',
    shopping: 'Shopping',
    other: 'Other',
  };

  if (loading) {
    return (
      <View style={{ flex: 1, justifyContent: 'center', alignItems: 'center', padding: theme.spacing(4) }}>
        <ActivityIndicator size="large" color={theme.colors.primary} />
        <Caption color={theme.colors.textSecondary} style={{ marginTop: theme.spacing(2) }}>
          Calculating analytics...
        </Caption>
      </View>
    );
  }

  if (analyticsError) {
    return (
      <View style={{ flex: 1, justifyContent: 'center', padding: theme.spacing(2) }}>
        <ErrorState
          variant="full"
          title="Couldn't load analytics"
          message={analyticsError}
          onRetry={() => setRetryNonce((value) => value + 1)}
          retryLabel="Retry"
        />
      </View>
    );
  }

  if (!statistics || expenses.length === 0) {
    return (
      <View style={{ flex: 1, justifyContent: 'center', alignItems: 'center', padding: theme.spacing(4) }}>
        <Ionicons name="stats-chart-outline" size={64} color={theme.colors.textSecondary} />
        <SubHeading style={{ marginTop: theme.spacing(2), textAlign: 'center' }}>
          No Analytics Available
        </SubHeading>
        <Body color={theme.colors.textSecondary} style={{ marginTop: theme.spacing(1), textAlign: 'center' }}>
          Add expenses to see spending analytics and trends.
        </Body>
      </View>
    );
  }

  const maxCategoryAmount = categorySpending.length > 0 
    ? Math.max(...categorySpending.map(c => c.amount))
    : 0;

  const maxDailyAmount = dailySpending.length > 0
    ? Math.max(...dailySpending.map(d => d.amount))
    : 0;

  const maxTrendAmount = spendingTrends.length > 0
    ? Math.max(...spendingTrends.map(t => t.amount))
    : 0;

  return (
    <ScrollView 
      style={{ flex: 1 }}
      contentContainerStyle={{ padding: theme.spacing(2), gap: theme.spacing(3) }}
      showsVerticalScrollIndicator={false}
    >
      {/* Trip Statistics Summary */}
      <Card>
        <SubHeading style={{ marginBottom: theme.spacing(2) }}>Trip Statistics</SubHeading>
        <View style={{ gap: theme.spacing(2) }}>
          <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' }}>
            <Body color={theme.colors.textSecondary}>Total Spent</Body>
            <ThemedText variant="bodyStrong" style={{ fontSize: 18 }}>
              {formatCurrency(statistics.totalSpentConverted, statistics.baseCurrency)}
            </ThemedText>
          </View>
          <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' }}>
            <Body color={theme.colors.textSecondary}>Average Per Day</Body>
            <ThemedText variant="bodyStrong">
              {formatCurrency(statistics.averagePerDay, statistics.baseCurrency)}
            </ThemedText>
          </View>
          <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' }}>
            <Body color={theme.colors.textSecondary}>Trip Duration</Body>
            <ThemedText variant="bodyStrong">{statistics.tripDuration} days</ThemedText>
          </View>
          <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' }}>
            <Body color={theme.colors.textSecondary}>Total Expenses</Body>
            <ThemedText variant="bodyStrong">{statistics.totalExpenses}</ThemedText>
          </View>
          <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' }}>
            <Body color={theme.colors.textSecondary}>Total Bookings</Body>
            <ThemedText variant="bodyStrong">{statistics.totalBookings}</ThemedText>
          </View>
          {statistics.mostExpensiveCategory && (
            <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' }}>
              <Body color={theme.colors.textSecondary}>Top Category</Body>
              <ThemedText variant="bodyStrong">
                {categoryLabels[statistics.mostExpensiveCategory] || statistics.mostExpensiveCategory}
              </ThemedText>
            </View>
          )}
        </View>
      </Card>

      {/* Category Spending Breakdown */}
      {categorySpending.length > 0 && (
        <Card>
          <SubHeading style={{ marginBottom: theme.spacing(2) }}>Spending by Category</SubHeading>
          <View style={{ gap: theme.spacing(2) }}>
            {categorySpending.map((category) => {
              const color = categoryColors[category.category] || categoryColors.other;
              const label = categoryLabels[category.category] || category.category;
              const barWidth = maxCategoryAmount > 0 ? (category.amount / maxCategoryAmount) * 100 : 0;

              return (
                <View key={category.category} style={{ gap: theme.spacing(0.5) }}>
                  <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' }}>
                    <View style={{ flexDirection: 'row', alignItems: 'center', gap: theme.spacing(1), flex: 1 }}>
                      <View
                        style={{
                          width: 12,
                          height: 12,
                          borderRadius: 6,
                          backgroundColor: color,
                        }}
                      />
                      <Body style={{ flex: 1 }}>{label}</Body>
                    </View>
                    <View style={{ alignItems: 'flex-end' }}>
                      <ThemedText variant="bodyStrong">
                        {formatCurrency(category.amount, category.currency)}
                      </ThemedText>
                      <Caption color={theme.colors.textSecondary}>
                        {category.percentage.toFixed(1)}% • {category.count} {category.count === 1 ? 'expense' : 'expenses'}
                      </Caption>
                    </View>
                  </View>
                  <View
                    style={{
                      height: 8,
                      backgroundColor: theme.colors.borderMuted,
                      borderRadius: theme.radii.sm,
                      overflow: 'hidden',
                    }}
                  >
                    <View
                      style={{
                        height: '100%',
                        width: `${barWidth}%`,
                        backgroundColor: color,
                        borderRadius: theme.radii.sm,
                      }}
                    />
                  </View>
                </View>
              );
            })}
          </View>
        </Card>
      )}

      {/* Daily Spending Chart */}
      {dailySpending.length > 0 && (
        <Card>
          <SubHeading style={{ marginBottom: theme.spacing(2) }}>Daily Spending</SubHeading>
          <View style={{ gap: theme.spacing(1.5) }}>
            {dailySpending.map((day) => {
              const date = new Date(day.date);
              const dateLabel = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
              const barHeight = maxDailyAmount > 0 ? (day.amount / maxDailyAmount) * 100 : 0;

              return (
                <View key={day.date} style={{ gap: theme.spacing(0.5) }}>
                  <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Caption color={theme.colors.textSecondary}>{dateLabel}</Caption>
                    <View style={{ alignItems: 'flex-end' }}>
                      <ThemedText variant="bodyStrong" style={{ fontSize: 14 }}>
                        {formatCurrency(day.amount, day.currency)}
                      </ThemedText>
                      <Caption color={theme.colors.textSecondary}>
                        {day.expenseCount} {day.expenseCount === 1 ? 'expense' : 'expenses'}
                      </Caption>
                    </View>
                  </View>
                  <View
                    style={{
                      height: 24,
                      backgroundColor: theme.colors.borderMuted,
                      borderRadius: theme.radii.sm,
                      overflow: 'hidden',
                      flexDirection: 'row',
                    }}
                  >
                    <View
                      style={{
                        height: '100%',
                        width: `${barHeight}%`,
                        backgroundColor: theme.colors.primary,
                        borderRadius: theme.radii.sm,
                      }}
                    />
                  </View>
                </View>
              );
            })}
          </View>
        </Card>
      )}

      {/* Spending Trends */}
      {spendingTrends.length > 0 && (
        <Card>
          <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: theme.spacing(2) }}>
            <SubHeading>Spending Trends</SubHeading>
            <View style={{ flexDirection: 'row', gap: theme.spacing(1) }}>
              <View
                style={{
                  paddingHorizontal: theme.spacing(1.5),
                  paddingVertical: theme.spacing(0.5),
                  borderRadius: theme.radii.sm,
                  backgroundColor: selectedPeriod === 'daily' ? theme.colors.primary + '20' : 'transparent',
                }}
              >
                <Caption
                  color={selectedPeriod === 'daily' ? theme.colors.primary : theme.colors.textSecondary}
                  onPress={() => setSelectedPeriod('daily')}
                  style={{ fontWeight: selectedPeriod === 'daily' ? '600' : '400' }}
                >
                  Daily
                </Caption>
              </View>
              <View
                style={{
                  paddingHorizontal: theme.spacing(1.5),
                  paddingVertical: theme.spacing(0.5),
                  borderRadius: theme.radii.sm,
                  backgroundColor: selectedPeriod === 'weekly' ? theme.colors.primary + '20' : 'transparent',
                }}
              >
                <Caption
                  color={selectedPeriod === 'weekly' ? theme.colors.primary : theme.colors.textSecondary}
                  onPress={() => setSelectedPeriod('weekly')}
                  style={{ fontWeight: selectedPeriod === 'weekly' ? '600' : '400' }}
                >
                  Weekly
                </Caption>
              </View>
            </View>
          </View>
          <View style={{ gap: theme.spacing(1.5) }}>
            {spendingTrends.map((trend) => {
              const barHeight = maxTrendAmount > 0 ? (trend.amount / maxTrendAmount) * 100 : 0;

              return (
                <View key={trend.period} style={{ gap: theme.spacing(0.5) }}>
                  <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Caption color={theme.colors.textSecondary}>{trend.period}</Caption>
                    <View style={{ alignItems: 'flex-end' }}>
                      <ThemedText variant="bodyStrong" style={{ fontSize: 14 }}>
                        {formatCurrency(trend.amount, statistics.baseCurrency)}
                      </ThemedText>
                      <Caption color={theme.colors.textSecondary}>
                        {trend.expenseCount} {trend.expenseCount === 1 ? 'expense' : 'expenses'}
                      </Caption>
                    </View>
                  </View>
                  <View
                    style={{
                      height: 24,
                      backgroundColor: theme.colors.borderMuted,
                      borderRadius: theme.radii.sm,
                      overflow: 'hidden',
                      flexDirection: 'row',
                    }}
                  >
                    <View
                      style={{
                        height: '100%',
                        width: `${barHeight}%`,
                        backgroundColor: theme.colors.primary,
                        borderRadius: theme.radii.sm,
                      }}
                    />
                  </View>
                </View>
              );
            })}
          </View>
        </Card>
      )}

      {/* Top Merchants */}
      {statistics.topMerchants.length > 0 && (
        <Card>
          <SubHeading style={{ marginBottom: theme.spacing(2) }}>Top Merchants</SubHeading>
          <View style={{ gap: theme.spacing(1.5) }}>
            {statistics.topMerchants.map((merchant, index) => (
              <View
                key={merchant.merchant}
                style={{
                  flexDirection: 'row',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  paddingVertical: theme.spacing(1),
                }}
              >
                <View style={{ flexDirection: 'row', alignItems: 'center', gap: theme.spacing(1.5), flex: 1 }}>
                  <View
                    style={{
                      width: 32,
                      height: 32,
                      borderRadius: 16,
                      backgroundColor: theme.colors.primary + '20',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <ThemedText variant="bodyStrong" color={theme.colors.primary} style={{ fontSize: 14 }}>
                      {index + 1}
                    </ThemedText>
                  </View>
                  <View style={{ flex: 1 }}>
                    <Body>{merchant.merchant}</Body>
                    <Caption color={theme.colors.textSecondary}>
                      {merchant.count} {merchant.count === 1 ? 'purchase' : 'purchases'}
                    </Caption>
                  </View>
                </View>
                <ThemedText variant="bodyStrong">
                  {formatCurrency(merchant.amount, statistics.baseCurrency)}
                </ThemedText>
              </View>
            ))}
          </View>
        </Card>
      )}

      {/* Top Locations */}
      {statistics.topLocations.length > 0 && (
        <Card>
          <SubHeading style={{ marginBottom: theme.spacing(2) }}>Top Locations</SubHeading>
          <View style={{ gap: theme.spacing(1.5) }}>
            {statistics.topLocations.map((location, index) => (
              <View
                key={location.location}
                style={{
                  flexDirection: 'row',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  paddingVertical: theme.spacing(1),
                }}
              >
                <View style={{ flexDirection: 'row', alignItems: 'center', gap: theme.spacing(1.5), flex: 1 }}>
                  <Ionicons name="location-outline" size={20} color={theme.colors.primary} />
                  <View style={{ flex: 1 }}>
                    <Body>{location.location}</Body>
                    <Caption color={theme.colors.textSecondary}>
                      {location.count} {location.count === 1 ? 'expense' : 'expenses'}
                    </Caption>
                  </View>
                </View>
                <ThemedText variant="bodyStrong">
                  {formatCurrency(location.amount, statistics.baseCurrency)}
                </ThemedText>
              </View>
            ))}
          </View>
        </Card>
      )}
    </ScrollView>
  );
}

